import type { SalesNode } from '../api/types';

// Hilfen für Vertriebsstruktur und Provisionen (Anteile in Basispunkten: 1 % = 100, Beträge in Cent).

export const FULL_SHARE = 10000;

const percentFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
const euroFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

export const pct = (bp: number) => `${percentFormat.format(bp / 100)} %`;
export const euro = (cents: number) => euroFormat.format(cents / 100);

// parsePercent liest „20“, „33,33“ oder „12.5“ als Basispunkte (null bei ungültiger Eingabe).
export function parsePercent(text: string): number | null {
  const v = Number(text.trim().replace('%', '').replace(',', '.'));
  if (text.trim() === '' || !Number.isFinite(v) || v < 0 || v > 100) return null;
  return Math.round(v * 100);
}

// parseEuro liest „500.000“, „500000,50“ oder „500 000 €“ als Cent (null bei ungültiger Eingabe).
export function parseEuro(text: string): number | null {
  const cleaned = text.replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const v = Number(cleaned);
  if (cleaned === '' || !Number.isFinite(v) || v < 0) return null;
  return Math.round(v * 100);
}

// levelLabel: Rolle eines Anteils aus Sicht des Abschlusses.
export function levelLabel(level: number | null | undefined, recipientType: string): string {
  if (recipientType === 'COMPANY') return 'Firma (Rest)';
  if (level === null || level === undefined) return 'Individuell';
  if (level === 0) return 'Abschließender Mitarbeiter';
  if (level === 1) return 'Direkte Upline';
  return `Upline ${level}. Ebene`;
}

// ownLevelLabel: Herkunft eines Anteils aus Sicht der Person (Provisionsübersicht).
export function ownLevelLabel(level: number | null | undefined): string {
  if (level === null || level === undefined) return 'Individuell';
  if (level === 0) return 'Eigener Abschluss';
  return `Struktur, ${level}. Ebene`;
}

export interface TreeItem {
  node: SalesNode;
  children: TreeItem[];
}

// buildTree ordnet die flache Liste (sortiert nach Tiefe) zu einem Baum; Wurzeln sind Knoten ohne Vorgesetzten in der Liste.
export function buildTree(nodes: SalesNode[]): TreeItem[] {
  const items = new Map(nodes.map((n) => [n.user_id, { node: n, children: [] as TreeItem[] }]));
  const roots: TreeItem[] = [];
  for (const n of nodes) {
    const item = items.get(n.user_id)!;
    const parent = n.parent_user_id ? items.get(n.parent_user_id) : undefined;
    if (parent) parent.children.push(item);
    else roots.push(item);
  }
  return roots;
}

// previewAmounts: Beträge wie auf dem Server – je Anteil abgerundet, Rundungsreste an die Firma.
export function previewAmounts(netCents: number, personShares: number[]): { persons: number[]; company: number } {
  const persons = personShares.map((bp) => Math.floor((netCents * bp) / FULL_SHARE));
  return { persons, company: netCents - persons.reduce((a, b) => a + b, 0) };
}

// levelShareLabel: Bezeichnung einer Ebene im Verteilungsplan.
export function levelShareLabel(index: number): string {
  if (index === 0) return 'Abschließender Mitarbeiter';
  if (index === 1) return 'Direkte Upline';
  return `${index}. Ebene darüber`;
}
